import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Flame, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get("/quiz").then((r) => { setQuestions(r.data.questions); setAnswers(r.data.answers || {}); }).catch(() => {}); }, []);

  const finish = async (final) => {
    setSaving(true);
    try {
      await api.put("/quiz", { answers: final });
      toast.success("Done! You'll now see compatibility scores on Discover");
      navigate("/discover");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); setSaving(false); }
  };

  const pick = (idx) => {
    const q = questions[step];
    const next = { ...answers, [q.id]: idx };
    setAnswers(next);
    if (step + 1 < questions.length) setTimeout(() => setStep(step + 1), 180);
    else finish(next);
  };

  if (!questions.length) return <div className="p-12 text-[#A0A0A0]">Loading…</div>;
  const q = questions[step];

  return (
    <div className="p-6 md:p-12 max-w-xl mx-auto" data-testid="quiz-page">
      <button data-testid="quiz-back" onClick={() => (step ? setStep(step - 1) : navigate(-1))} className="text-[#A0A0A0] hover:text-white flex items-center gap-2 mb-6">
        <ArrowLeft size={20} /> {step ? "Previous" : "Back"}
      </button>
      <div className="overline mb-1 flex items-center gap-2"><Flame size={12} className="text-[#D4AF37]" /> Desire quiz · 1 minute</div>
      <div className="flex items-center gap-3 mb-10">
        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <motion.div className="h-full gold-gradient" animate={{ width: `${(step / questions.length) * 100}%` }} />
        </div>
        <span className="text-xs text-[#A0A0A0]" data-testid="quiz-step">{step + 1} / {questions.length}</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.2 }}>
          <h2 className="font-heading text-3xl md:text-4xl mb-8" data-testid="quiz-question">{q.prompt}</h2>
          <div className="space-y-3">
            {q.options.map((opt, i) => (
              <button key={opt} data-testid={`quiz-option-${i}`} disabled={saving} onClick={() => pick(i)}
                className={`w-full text-left px-5 py-4 rounded-2xl border transition-colors ${answers[q.id] === i ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-[#2A2A2A] text-[#C8C8C8] hover:border-[#D4AF37]/60 hover:text-white"}`}>
                {opt}
              </button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
      {saving && <div className="mt-6 text-sm text-[#A0A0A0] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving your answers…</div>}
    </div>
  );
}
